import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { Grid, GridColumn, GridRow, Input, Select, SelectOptionType, Switch } from '@bp/ui-components';
import { useCreateSelectOptions } from '../../hooks/useCreateSelectOptions';
import { MultiValue, SingleValue } from 'react-select';
import { useAuthClaims } from '../../hooks/useAuthClaims';
import { GroupFormType } from './GroupForm';
import { FragmentType, getFragmentData, graphql } from 'client/gql';
import { usePermissionChecker } from 'hooks/usePermissionChecker';

const GroupFormBaseDataFieldsDocument = graphql(`
  fragment GroupFormBaseDataFields on Query {
    schoolYears(where: { organization: { uuid: $organizationUuid } }) {
      uuid
      __typename
      name
    }
    profiles(where: { organization: { uuid: $organizationUuid } }) {
      uuid
      __typename
      displayName
    }
    ...PermissionCheckerFields
  }
`);

type GroupFormBaseDataProps = {
  readonly: boolean;
  canCollaborate: boolean;
  forceUserAsAdmin?: boolean;
  onCollaborationChange: (isCollaboration: boolean) => void;
  query: FragmentType<typeof GroupFormBaseDataFieldsDocument> | undefined;
};

export const GroupFormBaseData = ({
  readonly,
  canCollaborate,
  forceUserAsAdmin,
  onCollaborationChange,
  query,
}: GroupFormBaseDataProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();

  const { handleChange, handleBlur, setFieldValue, values, errors, touched, setFieldTouched } =
    useFormikContext<GroupFormType>();

  const fragment = getFragmentData(GroupFormBaseDataFieldsDocument, query);
  const permissions = usePermissionChecker(fragment);
  const schoolyears = fragment?.schoolYears;
  const profiles = fragment?.profiles;

  const selectSchoolyearOpts = useCreateSelectOptions(schoolyears, 'uuid', 'name');
  const adminSingleOpts = useCreateSelectOptions([pimAuthClaims.getProfile()], 'uuid', 'displayName');
  const adminOpts = useCreateSelectOptions(profiles, 'uuid', 'displayName');

  const selectedSchoolyear = selectSchoolyearOpts.find((opt) => {
    return values.schoolYear ? opt.value === values.schoolYear : false;
  }) ?? { label: t('group.schoolyearSpanning'), value: 'schoolyearSpanning' };

  return (
    <Grid useFormGap>
      <GridRow spacingBottom='s'>
        <Input
          autoFocus
          label={t('common.designation')}
          name='name'
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.name}
          error={errors.name}
        />
      </GridRow>
      <GridRow spacingBottom='s' spacingTop='s'>
        <GridColumn width={6}>
          <Select
            label={t('common.schoolyear')}
            name='schoolyear'
            options={selectSchoolyearOpts}
            value={selectedSchoolyear}
            isClearable={selectSchoolyearOpts.some((opt) =>
              values.schoolYear ? opt.value === values.schoolYear : false,
            )}
            onChange={(event) => {
              const schoolyear = event as SingleValue<SelectOptionType>;
              setFieldTouched('schoolYear', true);
              setFieldValue('schoolYear', schoolyear?.value ?? null);
            }}
            onBlur={handleBlur}
            menuPosition='fixed'
          />
        </GridColumn>
        <GridColumn width={6}>
          <Select
            name='admins'
            label={t('collaborations.administration')}
            value={
              !canCollaborate
                ? adminOpts.find((v) => v.value === pimAuthClaims.getProfile().uuid)
                : [...adminOpts.filter((v) => values.admins.includes(v.value as string))]
            }
            options={!canCollaborate ? adminSingleOpts : adminOpts}
            onChange={(event) => {
              const adminOptions = event as MultiValue<SelectOptionType>;
              setFieldTouched('admins', true);
              setFieldValue(
                'admins',
                adminOptions.map((value) => value.value as string),
              );
            }}
            isMulti={canCollaborate}
            isSearchable={canCollaborate}
            readonly={!canCollaborate || forceUserAsAdmin}
            menuPosition='fixed'
            error={errors.admins as string}
          />
        </GridColumn>
      </GridRow>
      <GridRow spacingTop='s' spacingBottom='s'>
        <Input
          label={t('common.description')}
          name='description'
          onChange={handleChange}
          value={values.description}
          {...(errors.description &&
            touched.description && {
              error: errors.description,
            })}
        />
      </GridRow>
      <GridRow spacingTop='s'>
        <GridColumn width={6} offset='left' align='end'>
          <Switch
            name='collaborating'
            label={t('courses.collaborationCourse')}
            checked={values.collaborating}
            disabled={readonly || !canCollaborate || !permissions?.canCreateCollaborationGroups()}
            onChange={(event) => {
              setFieldValue('collaborating', event.target.checked);
              onCollaborationChange(event.target.checked);
            }}
            tooltip={canCollaborate ? t('collaborations.addHint') : undefined}
          />
        </GridColumn>
      </GridRow>
    </Grid>
  );
};
